import 'rxjs/add/observable/of';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';

import { Injectable } from '@angular/core';
import { Response, URLSearchParams } from '@angular/http';

import { AccountOption } from '../entities/account-option.entity';
import { IUserFilter, ListResult, User } from '../entities/index';
import { Log } from '../utils/index';
import { Api } from './api.service';
import { PAGINATION_NUMBER } from './constants.service';
import { CrudInterface } from './crud.interface';

@Injectable()
export class UserService implements CrudInterface<User> {
  currentUser: BehaviorSubject<User> = new BehaviorSubject<User>(new User());

  countries: [[string, string]] = null;

  constructor(protected http: Api) {}

  reloadUser(forceReload: boolean = false): void {
    this.http.get('myuser').subscribe(
      user => this.currentUser.next(user),
      err => this.clearCurrentUser()
    );
  }

  clearCurrentUser(): void {
    this.currentUser.next(new User());
  }

  @Log('green')
  list(search: string = null, page: number = 1, limit: number = PAGINATION_NUMBER, filters?: IUserFilter): Observable<ListResult<User>> {
    const params = new URLSearchParams();
    if (search) {
      params.set('search', search);
    }
    if (page) {
      params.set('page', String(page));
    }
    if (limit) {
      params.set('limit', String(limit));
    }
    if(filters) {
      params.setAll(this.http.buildUrlSearchParams(filters));
    }

    return this.http.get('user', { search: params });
  }

  @Log('green')
  get(id: number): Observable<User> {
    return this._returnUser(this.http.get(`user/${id}`));
  }

  @Log('green')
  create(user: User): Observable<User> {
    return this._returnUser(this.http.post('user', user));
  }

  @Log('green')
  update(user: User): Observable<User> {
    delete user.bioggy_status;  // Should not be modified by our back-office
    delete user.white_label;
    const userId = user.id;
    return this._returnUser(this.http.put(`user/${userId}`, user));
  }

  save(user: User): Observable<User> {
    delete user.bioggy_status;  // Should not be modified by our back-office
    delete user.white_label;
    const isNew = undefined === user.id;
    if (isNew) {
      return this.create(user);
    } else {
      return this.update(user);
    }
  }

  @Log('green')
  remove(user: User): Observable<Response> {
    const userId = user.id;
    return this.http.delete(`user/${userId}`);
  }

  @Log('green')
  removeTrackers(user: User): Observable<Response> {
    const userId = user.id;
    return this.http.delete(`tracker?user_id=${userId}`);
  }

  @Log('green')
  createDebugPassword(user: User): Observable<string> {
    const userId = user.id;
    return this.http.post(`user/${userId}/debug`, {}).map(res => res.debug_password);
  }

  @Log('green')
  updatePassword(user: User, password: string): Observable<User> {
    user.password = password;
    return this.update(user);
  }

  /**
   * Used to map a response from API to the User interface defined in
   * src/app/entities/user.entity.ts
   * 
   * @param action The response from a GET or PUT call (a User Object from API)
   * @returns a User Object
   */
  protected _returnUser(action: Observable<any>): Observable<User> {
    return action.map(user => {
        const userEntity = {
          id: user.id,
          firstname: user.firstname,
          lastname: user.lastname,
          mail: user.mail,
          password: user.password,
          is_admin: user.is_admin,
          role_retailer_id: user.role_retailer_id,
          role_site: user.role_site,
          valid: user.valid,
          login_attempt: user.login_attempt,
          country: user.country,
          city: user.city,
          postal_code: user.postal_code,
          address: user.address,
          need_subscription: user.need_subscription,
          connection_date: user.connection_date,
          promo_code: user.promo_code,
          short_code: user.short_code,
          is_security: user.is_security,
          disable_history: user.disable_history,
          is_b2b: user.is_b2b,
          phone: user.phone,
          sms: user.sms,
          white_label: user.white_label && user.white_label.code || user.white_label || null,
          language: user.language,
          creation_date: user.creation_date,
          site: user.site,
          bioggy_status: user.bioggy_status,
          activate_sponsoring: user.activate_sponsoring,
          account_option_offers: user.account_option_offers,
          account_options: user.account_options,
        }
        return userEntity;
    });
  }

  @Log('green')
  getCountries(): Observable<[[string, string]]> {
    if (!this.countries) {
      return this.http.localGet('/assets/locales/countries.en.json').map(countries => {
        countries.unshift(['', '']);
        this.countries = countries;
        return countries;
      });
    }

    return Observable.of(this.countries);
  }

  @Log('green')
  modifyAccountOptions(userId: number, options: AccountOption[]): Observable<AccountOption[]> {
    // TODO
    return;
  }
}



// WEBPACK FOOTER //
// /builds/weenect1/backofficev3/src/app/services/user.service.ts